import { cn } from '@/shared/cn';

/** Pulse-rect recipe shared with AdminDashboardSkeleton. */
export const SKELETON_BLOCK =
  'animate-pulse rounded border border-divider bg-zinc-50 dark:bg-zinc-900/40';

export type SkeletonBlockProps = {
  h?: string | number;
  w?: string | number;
  className?: string;
};

export function SkeletonBlock({ h, w, className }: SkeletonBlockProps) {
  return (
    <div
      className={cn(SKELETON_BLOCK, className)}
      style={{
        height: h,
        width: w,
      }}
      aria-hidden
    />
  );
}

export type SkeletonTextProps = {
  lines?: number;
  className?: string;
};

export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBlock
          key={i}
          className={cn('h-3', i === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}

export type SkeletonRowsProps = {
  count?: number;
  cols?: number;
  className?: string;
};

export function SkeletonRows({ count = 5, cols = 4, className }: SkeletonRowsProps) {
  return (
    <div className={cn('overflow-hidden rounded-2xl border border-divider', className)}>
      <div className={cn(SKELETON_BLOCK, 'h-10 rounded-none border-0 border-b border-divider')} />
      <div className="divide-y divide-divider">
        {Array.from({ length: count }).map((_, row) => (
          <div key={row} className="flex items-center gap-4 px-4 py-3" aria-hidden>
            {Array.from({ length: cols }).map((__, col) => (
              <SkeletonBlock
                key={col}
                className={cn(
                  'h-4 flex-1',
                  col === cols - 1 && 'max-w-8 flex-none',
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export type SkeletonCardGridProps = {
  count?: number;
  cols?: 1 | 2 | 3;
  className?: string;
};

const CARD_GRID_COLS = {
  1: 'grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 xl:grid-cols-3',
} as const;

export function SkeletonCardGrid({
  count = 6,
  cols = 3,
  className,
}: SkeletonCardGridProps) {
  return (
    <div
      className={cn('grid gap-3', CARD_GRID_COLS[cols], className)}
      aria-hidden
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonBlock key={i} className="h-[104px]" />
      ))}
    </div>
  );
}

export type SkeletonFormProps = {
  fields?: number;
  className?: string;
};

export function SkeletonForm({ fields = 4, className }: SkeletonFormProps) {
  return (
    <div className={cn('space-y-4', className)} aria-hidden>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <SkeletonBlock className="h-3 w-24" />
          <SkeletonBlock className="h-9 w-full" />
        </div>
      ))}
    </div>
  );
}
